import "./style.scss";
import "components/shared/DetailTabs/style.scss";

import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import ActionArrowLeft from "assets/img/svg/actionArrowLeft";
import Edit from "assets/img/svg/edit";
import { getValueOrDraft } from "components/Beneficiar/utils";
import { getPendingImports } from "components/ImportData/utils";
import EmptyState from "components/shared/EmptyState";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import ImagePopup from "components/shared/ImagePopup";
import InformationGrid from "components/shared/InformationGrid";
import LongTextBlock from "components/shared/LongTextBlock";
import TextSkeleton from "components/shared/TextSkeleton";
import ResponsiveButton from "components/shared/ResponsiveStyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import Tags from "components/shared/Tags";
import TitleCover from "components/shared/TitleCover";
import { getImportTypeTranslation } from "containers/BulkUpdatesContainer";
import { populateGridWithCustomFields } from "containers/CustomFieldsContainer/helpers";
import ScopeContainer from "containers/ScopeContext";
import TitleSalesContainer from "containers/TitleSalesContainer";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString } from "helpers/utils";
import useBindingKeyboard from "hooks/useBindingKeyboard";
import useDetailPreviewLoading from "hooks/useDetailPreviewLoading";
import useDetailTabSwitch, { getDetailTabPanelContentKey } from "hooks/useDetailTabSwitch";
import { PortalType } from "contexts/portal.context";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePageTitle from "hooks/usePageTitle";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchPeriods } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { deleteTag, fetchTags, fetchTitle, resetTitles } from "store/titleStore";

import variables from "../../assets/styles/_variables.module.scss";
import DataAtCreation from "./DataAtCreation";
import DataAtCreationSkeleton from "./DataAtCreation/DataAtCreationSkeleton";
import { TitlePriceTable } from "./TitlePriceTable";

export type TTags = { id: number; name: string; color: number };

interface ITitle {
    /** Edit a title status */
    editTitle: boolean;
    /** Set edit a title to true + param id of element to scroll to */
    openEditTitle: (step?: string) => void;
    /** When set, overrides the `:id` route param (work-context title view). */
    titleId?: string | number;
}

const Title = ({ editTitle, openEditTitle, titleId }: ITitle) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location: any = useLocation();

    const { title: currentTitle, titleStatus: titleLoadingStatus, titles } = useAppSelector((state) => state.titles);
    const selectedWork = useAppSelector((state) => state.works.selectedWork);
    const { activePeriod: activePeriodId } = useAppSelector((state) => state.period);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);

    const titleJobsPending = useMemo(() => {
        return getPendingImports({ jobs, fakeImports: pendingImports, type: ["title"] }).length > 0;
    }, [jobs, pendingImports]);

    // Fetch custom fields only of title resource:
    const { customFields } = useAppSelector((state) => state.customFields);

    const titleCustomFields = customFields.filter((item) => item.relatedResource === "title");

    const requestedTab = location.state != null && location.state.tab ? location.state.tab : "1";
    const initialTab = requestedTab === "2" ? "1" : requestedTab;
    const { value, tabEnterKey, containerRef, switchTab, handleTabChange } = useDetailTabSwitch(initialTab);
    const { id: idFromRoute } = useParams();
    const id = titleId != null ? String(titleId) : idFromRoute;
    const [avatarDialogType, setAvatarDialogType] = useState(false);
    const { getCompanySettings } = useCompanySettings();
    const usesMultipleBrand = getCompanySettings("uses_multiple_brands");
    const portalType = useContext(PortalType);
    const isBookPortal = portalType === "book";

    usePageTitle(t("Titles.Titles"));

    /**
     * Includes all fields within the General Info section of the form (minus those appearing on header)
     */
    const generalInfo = useMemo(() => {
        const generalCustomFields = titleCustomFields.filter(
            (field) => field.formLocation === "generalInfo" && field.valueType !== "longstring",
        );

        const displayedFields: Record<string, string> = {
            [t("Titles.PublicationDate")]: currentTitle?.releaseDate
                ? formatLocaleDateString({
                      date: currentTitle.releaseDate,
                      options: { numeric: true, timeZone: "UTC" },
                  })
                : "",
            [t("NewTitle.Format")]: currentTitle?.formatTypeName ?? "",
            [t("Titles.SKU")]: currentTitle?.sku ?? "",
        };

        if (currentTitle) {
            populateGridWithCustomFields({
                resourceData: currentTitle,
                resourceCustomFields: generalCustomFields,
                gridObject: displayedFields,
            });
        } else {
            generalCustomFields.forEach((field) => {
                displayedFields[field.name] = "";
            });
        }
        return displayedFields;
    }, [titleCustomFields, currentTitle]);

    /**
     * Includes all fields within the Other Info section of the form (native and custom)
     */
    const otherInformationFields = useMemo(() => {
        const otherInfoCustomFields = titleCustomFields.filter(
            (field) => field.formLocation === "otherInfo" && field.valueType !== "longstring",
        );

        const displayedFields: Record<string, string> = {
            [t("NewTitle.FormatInformation")]: currentTitle?.otherInformation?.format || "",
        };

        if (!isBookPortal) {
            displayedFields[t("NewPublication.AnalysisCode")] = currentTitle?.analysisCode || "";
            if (usesMultipleBrand) {
                displayedFields[t("Titles.AssociatedBrand")] = currentTitle?.associatedBrand || "";
            }
        }

        if (currentTitle?.otherInformation) {
            for (const infos in currentTitle.otherInformation) {
                if (infos !== "summary" && infos !== "notes" && infos !== "format") {
                    displayedFields[
                        t(`Titles.${infos.substring(0, 1).toUpperCase()}${infos.substring(1, infos.length)}`)
                    ] = currentTitle.otherInformation[infos] || "-";
                }
            }
        }

        if (currentTitle) {
            populateGridWithCustomFields({
                resourceData: currentTitle,
                resourceCustomFields: otherInfoCustomFields,
                gridObject: displayedFields,
            });
        } else {
            otherInfoCustomFields.forEach((field) => {
                displayedFields[field.name] = "";
            });
        }

        return displayedFields;
    }, [currentTitle, isBookPortal, titleCustomFields, usesMultipleBrand]);

    /**
     * Includes all fields that are longstring (either native or custom)
     */
    const textAreaFields = useMemo(() => {
        const longStringFields = titleCustomFields.filter((field) => field.valueType === "longstring");

        const displayedFields = [
            {
                title: t("NewTitle.Summary"),
                text: currentTitle?.otherInformation?.summary ? currentTitle?.otherInformation?.summary : "-",
            },
            {
                title: t("NewTitle.Notes"),
                text: currentTitle?.otherInformation?.notes ? currentTitle?.otherInformation?.notes : "-",
            },
        ];

        longStringFields.forEach((item) => {
            const fieldData = currentTitle?.customFields?.find((field) => field.customFieldTypeId === item.id);
            displayedFields.push({ title: item.name, text: fieldData?.value ? fieldData.value : "" });
        });

        return displayedFields;
    }, [currentTitle, titleCustomFields]);

    /**
     * Includes info about date of creation/edit of title
     */
    const crealoInfos = currentTitle && {
        [t("Summary.CreationDate")]: currentTitle.creationDate
            ? formatLocaleDateString({ date: currentTitle.creationDate, options: { numeric: true, timeZone: "UTC" } })
            : "-",
        [t("Titles.LatestEdition")]: currentTitle.lastModifiedDate
            ? formatLocaleDateString({
                  date: currentTitle.lastModifiedDate,
                  options: { numeric: true, timeZone: "UTC" },
              })
            : "-",
    };

    const handleChange = handleTabChange;
    const workId = currentTitle?.workId ?? selectedWork?.id ?? null;

    const handleOpenWork = () => {
        if (workId == null) return;
        navigate(`/works/${workId}`);
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchTitle(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        return () => {
            dispatch(resetTitles());
        };
    }, [dispatch]);

    useEffect(() => {
        !activePeriodId && dispatch(fetchPeriods());
    }, [activePeriodId, dispatch]);

    const handleDeleteTag = useCallback(
        (i: number) => {
            dispatch(deleteTag({ titleId: +id!, tagId: i })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    dispatch(fetchTags());
                }
            });
        },
        [dispatch, id],
    );

    const handleSwitchTabKeyboard = useCallback(
        (ev: KeyboardEvent) => {
            if (ev.code === "ArrowLeft" && parseInt(value) > 1) {
                switchTab((parseInt(value) - 1).toString());
            } else if (ev.code === "ArrowRight" && parseInt(value) < 3) {
                switchTab((parseInt(value) + 1).toString());
            }
            location.state = null;
        },
        [location, switchTab, value],
    );

    useBindingKeyboard({
        action: handleSwitchTabKeyboard,
        type: "keydown",
        touchToBind: ["ArrowLeft", "ArrowRight"],
    });

    const routeTitleId = id ? Number(id) : null;
    const { isHeaderLoading, isContentLoading } = useDetailPreviewLoading(
        routeTitleId,
        currentTitle?.id,
        titleLoadingStatus,
    );

    if (!isHeaderLoading && titles.length < 1 && !currentTitle) {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <EmptyState type="title" />
            </Box>
        );
    }

    const nameTranslationsExtraInfos = currentTitle?.nameTranslations?.map((translation) => ({
        label: t("NewTitle.Title") + " - " + t(`Locales.${translation.locale}`),
        value: translation.name,
    }));

    const ExtraInfosLabel = () => {
        return (
            <div className="extra-infos-container">
                <TranslateOutlinedIcon className="extra-infos-icon" />
                <span className="extra-infos-label">{t("Titles.NameTranslations")}</span>
            </div>
        );
    };

    return (
        <div
            ref={containerRef}
            className="title-container"
            style={editTitle ? { overflow: "hidden", height: "100vh" } : {}}
        >
            {currentTitle && !isHeaderLoading && (
                <ImagePopup
                    element={currentTitle}
                    avatarOpen={avatarDialogType}
                    setAvatarOpen={() => setAvatarDialogType(false)}
                />
            )}
            {(currentTitle || isHeaderLoading) && (
                <TabContext value={value}>
                    <div className="title-chrome">
                        <div className="title-header">
                            <div className="title-left-content">
                                <div
                                    onClick={() =>
                                        !isHeaderLoading && currentTitle?.avatarUrl && setAvatarDialogType(true)
                                    }
                                    style={{
                                        cursor: !isHeaderLoading && currentTitle?.avatarUrl ? "zoom-in" : undefined,
                                    }}
                                >
                                    <TitleCover
                                        title={isHeaderLoading ? "" : currentTitle?.name ?? ""}
                                        coverUrl={isHeaderLoading ? undefined : currentTitle?.avatarUrl}
                                        style={{ width: 85, fontSize: "25px" }}
                                    />
                                </div>
                                <div className="title-header-base-data">
                                    <p className="title-header-eyebrow">{t("Titles.TitleLabel")}</p>
                                    <h2>{isHeaderLoading ? <TextSkeleton width="12em" /> : currentTitle?.name}</h2>
                                    <div className="divided-blocks">
                                        {isHeaderLoading ? (
                                            <div className="info">
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                                    {t("Titles.ISBN")} <TextSkeleton width="8em" />
                                                </span>
                                            </div>
                                        ) : (
                                            currentTitle?.ean13 && (
                                                <div className="info">
                                                    <span
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "0.25rem",
                                                        }}
                                                    >
                                                        {t("Titles.ISBN")}{" "}
                                                        {getValueOrDraft(currentTitle.ean13) || <DraftEan13 />}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                    <div className="tag-panel">
                                        {isHeaderLoading ? (
                                            <TextSkeleton width="6em" />
                                        ) : (
                                            currentTitle && (
                                                <Tags
                                                    elementId={currentTitle.id}
                                                    elementType="titles"
                                                    onDelete={handleDeleteTag}
                                                    tags={currentTitle.tags}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="title-header-buttons">
                                <ScopeContainer
                                    extraConditions={[!titleJobsPending, !isContentLoading]}
                                    errorProps={{ disabled: true }}
                                >
                                    {(props) => (
                                        <ResponsiveButton
                                            onClick={() => openEditTitle()}
                                            {...props}
                                            tooltipText={
                                                titleJobsPending
                                                    ? t("AsyncUploads.PendingTooltip", {
                                                          element: getImportTypeTranslation("titles"),
                                                      })
                                                    : ""
                                            }
                                        >
                                            <Edit />
                                            <span>{t("NewTitle.EditTitle")}</span>
                                        </ResponsiveButton>
                                    )}
                                </ScopeContainer>
                                {workId != null && (
                                    <ResponsiveButton disabled={isHeaderLoading} onClick={handleOpenWork}>
                                        <ActionArrowLeft />
                                        <span>{t("Works.SeeWork")}</span>
                                    </ResponsiveButton>
                                )}
                            </div>
                        </div>
                        <div className="title-tabs">
                            <StyledTabs value={value} onChange={handleChange} textColor="inherit">
                                <Tab label={t("Titles.GeneralInformation")} value="1" />
                                <Tab label={t("Titles.DataAtCreation")} value="3" />
                                <Tab label={t("Titles.SalesData")} value="4" />
                            </StyledTabs>
                        </div>
                    </div>

                    {/* General Info */}
                    <TabPanel value="1" className="detail-tab-panel-root">
                        <div
                            className="detail-tab-panel-content"
                            key={getDetailTabPanelContentKey("1", value, tabEnterKey)}
                        >
                            <div className="detail-tab-body">
                                <div className="title-general-info">
                                    <InformationGrid
                                        data={generalInfo}
                                        title={t("Titles.GeneralInformation")}
                                        extraInfosLines={isContentLoading ? undefined : nameTranslationsExtraInfos}
                                        extraInfosLabel={<ExtraInfosLabel />}
                                        isLoading={isContentLoading}
                                    />
                                    <div className="tab-content-bloc">
                                        <div className="info-block-with-doc">
                                            <h3 className="information-title" style={{ paddingRight: "0px" }}>
                                                {t("Titles.PricePerChannel")}
                                            </h3>
                                            <ExpandedHelpdeskIcon
                                                link="https://helpdesk.crealo.app/fr/article/comment-ajouter-des-ean13isbn-sur-la-fiche-dun-titre-1ezh87g/"
                                                text={t("Titles.PricePerChannelDoc")}
                                                transparent
                                            />
                                        </div>
                                        <TitlePriceTable isLoading={isContentLoading} />
                                    </div>
                                    <InformationGrid
                                        data={otherInformationFields}
                                        title={t("NewTitle.OtherInformation")}
                                        isLoading={isContentLoading}
                                    />
                                    {textAreaFields.map((field) => (
                                        <LongTextBlock
                                            text={field.text}
                                            title={field.title}
                                            key={`title.longstring.${field.title.trim()}`}
                                            isLoading={isContentLoading}
                                        />
                                    ))}
                                    <InformationGrid
                                        data={
                                            crealoInfos ?? {
                                                [t("Summary.CreationDate")]: "",
                                                [t("Titles.LatestEdition")]: "",
                                            }
                                        }
                                        title={t("Titles.DateInfo")}
                                        style={{ marginTop: "32px" }}
                                        isLoading={isContentLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    {/* Data at creation */}
                    <TabPanel value="3" className="detail-tab-panel-root">
                        <div
                            className="detail-tab-panel-content"
                            key={getDetailTabPanelContentKey("3", value, tabEnterKey)}
                        >
                            <div className="detail-tab-body">
                                {isContentLoading ? (
                                    <DataAtCreationSkeleton />
                                ) : currentTitle?.startingChannelsData?.length === 0 &&
                                  currentTitle.initialInventory === 0 ? (
                                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                                        <span style={{ color: variables["foreground"] }}>
                                            {t("Titles.NoDataAtCreation")}
                                        </span>
                                    </div>
                                ) : currentTitle ? (
                                    <DataAtCreation title={currentTitle} navigate={() => openEditTitle(".step-2")} />
                                ) : null}
                            </div>
                        </div>
                    </TabPanel>

                    {/* Sales data */}
                    <TabPanel value="4" className="detail-tab-panel-root">
                        <div
                            className="detail-tab-panel-content"
                            key={getDetailTabPanelContentKey("4", value, tabEnterKey)}
                        >
                            {isContentLoading || !currentTitle ? (
                                <div className="detail-tab-body">
                                    <TextSkeleton width="100%" />
                                    <TextSkeleton width="90%" />
                                </div>
                            ) : (
                                <div className="detail-tab-body">
                                    <TitleSalesContainer
                                        titleId={+(id ?? currentTitle.id)}
                                        titlePrice={currentTitle.price}
                                        titleChannelsData={currentTitle.overriddenChannelsData}
                                    />
                                </div>
                            )}
                        </div>
                    </TabPanel>
                </TabContext>
            )}
        </div>
    );
};

export const DraftEan13 = () => {
    const [t] = useTranslation();
    return (
        <SmallBody color="grey" style={{ fontWeight: "300", fontStyle: "italic" }}>
            {t("Titles.NotSpecified")}
        </SmallBody>
    );
};

export default Title;
